<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronUp, Sparkles, X } from '@lucide/vue'
import type { SurveyQuestion } from '../types'

const props = withDefaults(defineProps<{
  question: SurveyQuestion
  index: number
  total: number
  dimensions?: Array<{ key: string; label: string; color: string }>
  modelValue?: unknown
  variant?: 'A' | 'B'
  error?: string
  instanceId?: string
}>(), {
  dimensions: () => [],
  variant: 'A',
  error: '',
  instanceId: 'survey',
})

const emit = defineEmits<{
  'update:modelValue': [value: unknown]
  interact: []
}>()

const promptExpanded = ref(false)

const questionCopy = computed(() => {
  const raw = props.question.prompt.trim()
  const codeMatch = raw.match(/^([A-Za-z]+\d+)\s*[|｜]\s*/)
  const withoutCode = codeMatch ? raw.slice(codeMatch[0].length).trim() : raw
  const optionMatch = withoutCode.match(/\s*(选项\s*[A-Za-zＡ-Ｚ])\s*[：:]\s*/)
  if (!optionMatch || optionMatch.index === undefined) {
    return { code: codeMatch?.[1] ?? '', context: withoutCode, optionLabel: '', optionText: '' }
  }
  return {
    code: codeMatch?.[1] ?? '',
    context: withoutCode.slice(0, optionMatch.index).trim(),
    optionLabel: optionMatch[1].replace(/\s+/g, ' '),
    optionText: withoutCode.slice(optionMatch.index + optionMatch[0].length).trim(),
  }
})

const lengthClass = computed(() => ({
  'is-long': questionCopy.value.context.length > 70,
  'is-very-long': questionCopy.value.context.length > 140,
  'is-collapsible-prompt': collapsiblePrompt.value,
  'has-compact-choices': compactChoices.value,
  'has-focus-option': Boolean(questionCopy.value.optionText),
}))
const collapsiblePrompt = computed(() => questionCopy.value.context.length > 75)
const compactChoices = computed(() => {
  if (props.question.type !== 'single' && props.question.type !== 'multi') return false
  const options = props.question.options ?? []
  return options.length >= 2 && options.length <= 6 && options.every((option) => option.label.trim().length <= 12)
})
const compactMatrix = computed(() => {
  if (props.question.type !== 'matrix_single' && props.question.type !== 'matrix_multi') return false
  const columns = props.question.columns ?? []
  return columns.length >= 2 && columns.length <= 7 && columns.every((column) => column.label.trim().length <= 7)
})
const dimensionLabel = computed(() => props.dimensions.find((item) => item.key === props.question.dimension)?.label || '体验问题')
const ranking = computed(() => Array.isArray(props.modelValue) ? props.modelValue as string[] : [])

watch(() => props.question.id, () => {
  promptExpanded.value = false
})

function update(value: unknown) {
  emit('update:modelValue', value)
  emit('interact')
}

function scaleValues() {
  if (props.question.type === 'slider') return [1, 2, 3, 4, 5]
  const min = props.question.min ?? 1
  const max = props.question.max ?? 5
  return Array.from({ length: max - min + 1 }, (_, index) => min + index)
}

function sliderLabel(value: unknown) {
  if (typeof value !== 'number') return '等待你的感觉'
  if (value < 180) return props.question.leftLabel || '更靠近左侧'
  if (value < 420) return '稍微靠近左侧'
  if (value < 580) return '恰好在中间'
  if (value < 820) return '稍微靠近右侧'
  return props.question.rightLabel || '更靠近右侧'
}

function toggleMulti(value: string) {
  const selected = Array.isArray(props.modelValue) ? [...props.modelValue as string[]] : []
  const index = selected.indexOf(value)
  if (index >= 0) selected.splice(index, 1)
  else selected.push(value)
  update(selected)
}

function setMatrix(row: string, value: string, multi: boolean) {
  const matrix = props.modelValue && typeof props.modelValue === 'object'
    ? { ...props.modelValue as Record<string, unknown> }
    : {}
  if (multi) {
    const selected = Array.isArray(matrix[row]) ? [...matrix[row] as string[]] : []
    const index = selected.indexOf(value)
    if (index >= 0) selected.splice(index, 1)
    else selected.push(value)
    matrix[row] = selected
  } else {
    matrix[row] = value
  }
  update(matrix)
}

function addRank(value: string) {
  if (ranking.value.includes(value)) return
  update([...ranking.value, value])
}

function moveRank(index: number, direction: -1 | 1) {
  const values = [...ranking.value]
  const next = index + direction
  if (next < 0 || next >= values.length) return
  ;[values[index], values[next]] = [values[next], values[index]]
  update(values)
}

function removeRank(value: string) {
  update(ranking.value.filter((item) => item !== value))
}
</script>

<template>
  <section class="question-card" :class="lengthClass">
    <div class="question-meta">
      <div class="question-count"><span>{{ String(index + 1).padStart(2, '0') }}</span><i/>{{ String(total).padStart(2, '0') }}</div>
      <p v-if="question.dimension" class="question-dimension"><Sparkles :size="14"/>{{ dimensionLabel }}</p>
    </div>
    <div class="question-copy" :class="{ 'has-code': questionCopy.code, 'prompt-expanded': promptExpanded }">
      <span v-if="questionCopy.code" class="question-code">{{ questionCopy.code }}</span>
      <h1>{{ questionCopy.context }}</h1>
      <div v-if="questionCopy.optionText" class="question-option-focus">
        <b>{{ questionCopy.optionLabel }}</b>
        <p>{{ questionCopy.optionText }}</p>
      </div>
    </div>
    <button v-if="collapsiblePrompt" class="question-prompt-toggle" type="button" :aria-expanded="promptExpanded" @click="promptExpanded = !promptExpanded">
      <ChevronUp v-if="promptExpanded"/><ChevronDown v-else/>
      {{ promptExpanded ? '收起完整题干' : '展开完整题干' }}
    </button>
    <p v-if="question.hint" class="question-hint">{{ question.hint }}</p>
    <p v-if="error" class="question-error" role="alert">{{ error }}</p>

    <div class="answer-area" :class="`answer-${question.type}`">
      <template v-if="question.type === 'slider' && variant === 'A'">
        <div class="slider-value">{{ sliderLabel(modelValue) }}</div>
        <input class="lumi-slider" type="range" min="0" max="1000" step="1" :value="(modelValue as number) ?? 500" :style="{ '--value': `${((modelValue as number) ?? 500) / 10}%` }" @input="update(Number(($event.target as HTMLInputElement).value))"/>
        <div class="slider-anchors"><span>{{ question.leftLabel || '完全不符合' }}</span><i>轻轻拖动光点</i><span>{{ question.rightLabel || '完全符合' }}</span></div>
      </template>

      <template v-else-if="question.type === 'scale' || (question.type === 'slider' && variant === 'B')">
        <div class="scale-options">
          <button v-for="(value, valueIndex) in scaleValues()" :key="value" type="button" :class="{ selected: modelValue === (question.type === 'slider' ? valueIndex * (1000 / Math.max(1, scaleValues().length - 1)) : value) }" @click="update(question.type === 'slider' ? valueIndex * (1000 / Math.max(1, scaleValues().length - 1)) : value)">
            <span>{{ value }}</span><small>{{ valueIndex === 0 ? (question.leftLabel || '很不符合') : valueIndex === scaleValues().length - 1 ? (question.rightLabel || '很符合') : valueIndex === Math.floor(scaleValues().length / 2) ? '中间' : '' }}</small>
          </button>
        </div>
      </template>

      <div v-else-if="question.type === 'single'" class="choice-list">
        <button v-for="(option, optionIndex) in question.options" :key="option.value" type="button" :class="{ selected: modelValue === option.value }" @click="update(option.value)">
          <span class="choice-key">{{ String.fromCharCode(65 + optionIndex) }}</span><span>{{ option.label }}</span><Check class="choice-check"/>
        </button>
      </div>

      <div v-else-if="question.type === 'multi'" class="choice-list">
        <button v-for="(option, optionIndex) in question.options" :key="option.value" type="button" :class="{ selected: ((modelValue as string[]) || []).includes(option.value) }" @click="toggleMulti(option.value)">
          <span class="choice-key">{{ String.fromCharCode(65 + optionIndex) }}</span><span>{{ option.label }}</span><Check class="choice-check"/>
        </button>
      </div>

      <div v-else-if="question.type === 'matrix_single' || question.type === 'matrix_multi'" class="matrix-answer" :class="{ 'is-compact-scale': compactMatrix }" :style="{ '--columns': question.columns?.length || 1 }">
        <div class="matrix-head"><span>题项</span><b v-for="column in question.columns" :key="column.value">{{ column.label }}</b></div>
        <div v-for="row in question.rows" :key="row.value" class="matrix-row">
          <strong>{{ row.label }}</strong>
          <label v-for="column in question.columns" :key="column.value">
            <input :type="question.type === 'matrix_single' ? 'radio' : 'checkbox'" :name="`${instanceId}-${question.id}-${row.value}`" :aria-label="`${row.label}：${column.label}`" :checked="question.type === 'matrix_single' ? (modelValue as Record<string,string>)?.[row.value] === column.value : ((modelValue as Record<string,string[]>)?.[row.value] || []).includes(column.value)" @change="setMatrix(row.value, column.value, question.type === 'matrix_multi')"/>
            <span>{{ column.label }}</span>
          </label>
        </div>
      </div>

      <div v-else-if="question.type === 'ranking'" class="ranking-answer">
        <div class="ranked-list">
          <article v-for="(value, rankIndex) in ranking" :key="value"><b>{{ rankIndex + 1 }}</b><span>{{ question.options?.find(item => item.value === value)?.label }}</span><button type="button" title="上移一位" :aria-label="`将“${question.options?.find(item => item.value === value)?.label || value}”上移一位`" :disabled="rankIndex === 0" @click="moveRank(rankIndex, -1)"><ArrowUp/></button><button type="button" title="下移一位" :aria-label="`将“${question.options?.find(item => item.value === value)?.label || value}”下移一位`" :disabled="rankIndex === ranking.length - 1" @click="moveRank(rankIndex, 1)"><ArrowDown/></button><button type="button" title="移出排序" :aria-label="`将“${question.options?.find(item => item.value === value)?.label || value}”移出排序`" @click="removeRank(value)"><X/></button></article>
        </div>
        <div class="unranked-options"><button v-for="option in question.options?.filter(item => !ranking.includes(item.value))" :key="option.value" type="button" @click="addRank(option.value)">+ {{ option.label }}</button></div>
      </div>

      <input v-else-if="question.type === 'number'" class="text-answer compact-answer" type="number" :min="question.min" :max="question.max" :step="question.step || 1" :value="modelValue as number" @input="update(Number(($event.target as HTMLInputElement).value))"/>
      <input v-else-if="question.type === 'date'" class="text-answer compact-answer" type="date" :value="modelValue as string" @input="update(($event.target as HTMLInputElement).value)"/>
      <textarea v-else class="text-answer" rows="5" maxlength="1000" placeholder="在这里写下你的想法（请勿填写身份信息）" :value="modelValue as string" @input="update(($event.target as HTMLTextAreaElement).value)"/>
    </div>
  </section>
</template>
